<template>
  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      title="方案分类由门户方案管理配置；列表页筛选项与导航左栏均读已发布 tab。"
    />
    <div class="mb-12px flex flex-wrap gap-8px">
      <el-button v-hasPermi="['portal:solution:create']" type="primary" @click="openForm('create')">
        新增分类
      </el-button>
      <el-button @click="getList">刷新</el-button>
    </div>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column label="展示名" prop="name" min-width="140" />
      <el-table-column label="key" prop="tabKey" min-width="120" />
      <el-table-column label="排序" prop="sort" width="80" align="center" />
      <el-table-column label="状态" width="90" align="center">
        <template #default="{ row }">
          <el-tag v-if="row.status === 1" type="success">发布</el-tag>
          <el-tag v-else type="info">未发布</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <el-button
            v-hasPermi="['portal:solution:update']"
            link
            type="primary"
            @click="openForm('update', row)"
          >
            编辑
          </el-button>
          <el-button
            v-hasPermi="['portal:solution:delete']"
            link
            type="danger"
            @click="handleDelete(row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      :total="total"
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      @pagination="getList"
    />
  </ContentWrap>

  <el-dialog v-model="dialogVisible" :title="dialogTitle" width="520px" destroy-on-close>
    <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
      <el-form-item label="展示名" prop="name">
        <el-input v-model="form.name" placeholder="如：政务解决方案" />
      </el-form-item>
      <el-form-item label="key" prop="tabKey">
        <el-input
          v-model="form.tabKey"
          :disabled="formType === 'update'"
          placeholder="如 gov（小写字母开头，a-z0-9_-）"
        />
      </el-form-item>
      <el-form-item label="排序" prop="sort">
        <el-input-number v-model="form.sort" :min="0" :max="9999" />
      </el-form-item>
      <el-form-item label="状态">
        <el-radio-group v-model="form.status">
          <el-radio :value="1">发布</el-radio>
          <el-radio :value="0">草稿</el-radio>
          <el-radio :value="2">下线</el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="formLoading" @click="submitForm">保存</el-button>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { ElMessageBox } from 'element-plus'
import type { FormInstance, FormRules } from 'element-plus'
import * as Api from '@/api/system/portal-solution'
import { useMessage } from '@/hooks/web/useMessage'

defineOptions({ name: 'PortalSolutionTab' })

const TAB_KEY_PATTERN = /^[a-z][a-z0-9_-]{1,31}$/

const message = useMessage()
const loading = ref(false)
const list = ref<Api.PortalSolutionTabVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10
})
const dialogVisible = ref(false)
const dialogTitle = ref('')
const formType = ref<'create' | 'update'>('create')
const formLoading = ref(false)
const formRef = ref<FormInstance>()
const editingId = ref<number>()
/** 编辑时保留库中 listPage，避免把 industry 种子覆盖成 false */
const editingListPage = ref(false)

const form = reactive({
  name: '',
  tabKey: '',
  sort: 100,
  status: 1
})

const rules: FormRules = {
  name: [{ required: true, message: '请输入展示名', trigger: 'blur' }],
  tabKey: [
    { required: true, message: '请输入 key', trigger: 'blur' },
    {
      pattern: TAB_KEY_PATTERN,
      message: '小写字母开头，仅 a-z0-9_-，长度 2～32',
      trigger: 'blur'
    }
  ]
}

async function getList() {
  loading.value = true
  try {
    const data = await Api.getPortalSolutionTabPage({ ...queryParams })
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

function openForm(type: 'create' | 'update', row?: Api.PortalSolutionTabVO) {
  formType.value = type
  dialogTitle.value = type === 'create' ? '新增方案分类' : '编辑方案分类'
  editingId.value = row?.id
  if (row) {
    form.name = row.name
    form.tabKey = row.tabKey
    form.sort = row.sort ?? 100
    form.status = row.status ?? 1
    editingListPage.value = !!row.listPage
  } else {
    Object.assign(form, { name: '', tabKey: '', sort: 100, status: 1 })
    editingListPage.value = false
  }
  dialogVisible.value = true
}

async function submitForm() {
  await formRef.value?.validate()
  formLoading.value = true
  try {
    const payload: Api.PortalSolutionTabVO = {
      id: editingId.value,
      name: form.name,
      tabKey: form.tabKey.trim().toLowerCase(),
      listPage: formType.value === 'create' ? false : editingListPage.value,
      sort: form.sort,
      status: form.status
    }
    if (formType.value === 'create') {
      delete payload.id
      await Api.createPortalSolutionTab(payload)
      message.success('已创建')
    } else {
      await Api.updatePortalSolutionTab(payload)
      message.success('已保存')
    }
    dialogVisible.value = false
    await getList()
  } finally {
    formLoading.value = false
  }
}

async function handleDelete(id?: number) {
  if (!id) return
  await ElMessageBox.confirm('确认删除该分类？其下入口不会自动删除。', '提示', { type: 'warning' })
  await Api.deletePortalSolutionTab(id)
  message.success('已删除')
  await getList()
}

onMounted(getList)
</script>
